import { useMutation, useQuery } from '@tanstack/react-query';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, useTheme } from 'styled-components';

import { UpdateParameters, UsersResponse } from '../../../../../typings/api/skymap/rest/v0/user';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { formattedDate, formattedDateTime } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { useDialog } from '../../hooks/use-dialog';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useNonce } from '../../hooks/use-nonce';
import { Button } from '../button/button';
import { CheckBox } from '../check-box/check-box';
import { DataTable, DataTableColumns, DataTableSortType } from '../data-table/data-table';
import { Dialog } from '../dialog/dialog';
import { Icon } from '../icon/icon';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { InvitationList } from './invitation-list';

const pageSize = 15;
type Item = {
  statusIcon: ReactNode;
  status: 'enabled' | 'disabled';
  id: string;
  enabled: boolean;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  isOrganisationAdmin: boolean;
  createdAt: Date;
  updatedAt: Date;
};

const searchKeys = ['displayName', 'email', 'status'] as const;

function filterItems(items: Item[], searchText: string): Item[] {
  const text = searchText.trim().toLowerCase();

  if (text.length === 0) {
    return items;
  }
  const parts = text.split(' ');

  return items.filter((item) =>
    searchKeys.some((key) => parts.some((searchPart) => item[key].includes(searchPart))),
  );
}

export function UserStatusIcon(enabled: boolean, theme: DefaultTheme): ReactNode {
  return (
    <Icon
      color={enabled ? theme.color.green : theme.color.gray.medium}
      fixedWidth={true}
      icon={['fad', 'user-alt']}
    />
  );
}

const columns: DataTableColumns<Item> = {
  statusIcon: {
    title: 'Status',
    alignment: 'center',
    width: '45px',
    sortableBy: { type: DataTableSortType.STRING, columnKey: 'status' },
    unfilterable: true,
  },
  displayName: {
    sortableBy: { type: DataTableSortType.STRING },
    unfilterable: true,
    title: 'Namn',
  },
  email: {
    sortableBy: { type: DataTableSortType.STRING },
    unfilterable: true,
    title: 'E-post',
  },
  createdAt: {
    sortableBy: { type: DataTableSortType.DATE },
    unfilterable: true,
    title: 'Skapad',
    formatter: formattedDate,
  },
  updatedAt: {
    sortableBy: { type: DataTableSortType.DATE },
    unfilterable: true,
    title: 'Senast ändrad',
    formatter: formattedDateTime,
  },
  isOrganisationAdmin: {
    sortableBy: { type: DataTableSortType.DATE },
    unfilterable: true,
    title: 'OrgAdmin',
    formatter: (value) => (value ? 'Ja' : 'Nej'),
  },
};

function mapUserModelToItem(user: UsersResponse['data'][number], theme: DefaultTheme): Item {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    enabled: user.enabled,
    displayName: `${user.firstName} ${user.lastName}`,
    isOrganisationAdmin: user.isOrganizationAdmin,
    status: user.enabled ? 'enabled' : 'disabled',
    statusIcon: UserStatusIcon(user.enabled, theme),
    createdAt: new Date(user.createdAt),
    updatedAt: new Date(user.updatedAt),
    phoneNumber: user.phoneNumber ?? '',
  };
}

async function fetchAllUsers(): Promise<UsersResponse['data']> {
  const result = await SkyMapAxiosServiceFactory.instance.createUserServiceV0().fetchAllUsers({});
  return result.data;
}

/**
 * Fetches a list of basic project information for listing the projects in the project list.
 */
function useGetUsersQuery() {
  const { handleError, clearErrors, buildErrorList } = useErrorHandling();
  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['getAllUsers'],
    queryFn: fetchAllUsers,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Define effect on error outside of mutation to avoid infinite render loop.
  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  const errorList = useMemo(
    () =>
      buildErrorList({
        infoBoxProps: {
          width: 'fit-content',
        },
      }),
    [buildErrorList],
  );

  return {
    users: data ?? [],
    errorList,
    isLoading,
  };
}

export const OrgAdminUserList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchText, setSearchText] = useState('');
  const editUserDialog = useDialog();
  const [selectedUser, setSelectedUser] = useState<Item>();
  const { users, isLoading, errorList } = useGetUsersQuery();
  const [nonce, refreshUserList] = useNonce();

  const items = useMemo<Item[]>(() => {
    return nonce >= 0 ? users.map((user) => mapUserModelToItem(user, theme)) : [];
  }, [users, nonce, theme]);

  const filteredItems = useMemo<Item[]>(() => filterItems(items, searchText), [items, searchText]);

  return (
    <OverlayLoader visible={isLoading}>
      <Stack spacing={1}>
        <h5>Inbjudningar</h5>
        <InvitationList />
        <h5>Användare</h5>
        {errorList}
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <TextBox
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
          />
        </Stack>
        <DataTable
          columns={columns}
          fixedLayout={true}
          items={filteredItems}
          pageSize={pageSize}
          onRowClicked={(item) => {
            setSelectedUser(item);
            editUserDialog.show();
          }}
        />
      </Stack>
      {editUserDialog.render(
        <EditUserDialog
          dialog={editUserDialog}
          user={selectedUser}
          onUserUpdated={(data) => {
            const userIndex = users.findIndex((x) => x.id === data.id);
            if (userIndex >= 0) {
              users[userIndex] = data;
              refreshUserList();
            }
          }}
        />,
      )}
    </OverlayLoader>
  );
};

function useUpdateUserMutation() {
  const { handleError, buildErrorList } = useErrorHandling();
  const mutation = useMutation({
    mutationFn: (params: UpdateParameters['body']) => {
      return SkyMapAxiosServiceFactory.instance.createUserServiceV0().update({
        body: params,
      });
    },
    retry: false,
    networkMode: 'always',
    onError: (error) => handleError(error),
  });

  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    updateUser: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
}

const EditUserDialog = (props: {
  user?: Item;
  dialog: ReturnType<typeof useDialog>;
  onUserUpdated: (data: UsersResponse['data'][number]) => void;
}) => {
  const { updateUser, isPending, errorList } = useUpdateUserMutation();
  const [enabled, setEnabled] = useState<boolean>(props.user?.enabled ?? false);
  const [firstName, setFirstName] = useState<string>(props.user?.firstName ?? '');
  const [lastName, setLastName] = useState<string>(props.user?.lastName ?? '');
  const { t } = useTranslation();
  const onClose = useCallback(() => {
    if (isPending) {
      return;
    }

    props.dialog.hide();
  }, [props.dialog, isPending]);

  if (!isDefined(props.user)) {
    return null;
  }

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} maxHeight="expand" onClose={onClose}>
      {{
        header: `Editera ${props.user.displayName}`,
        content: (
          <OverlayLoader visible={isPending}>
            <Stack spacing={1}>
              <LabelledContainer fontWeight={600} text={'Förnamn'}>
                <TextBox value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'Efternamn'}>
                <TextBox value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'Aktiverad'}>
                <CheckBox checked={enabled} onChange={(e) => setEnabled(!enabled)} />
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'ID'}>
                {props.user.id}
              </LabelledContainer>
              <LabelledContainer
                fontWeight={600}
                text={t('newCompanyAdmin.newUser.form.email', { ns: 'components' })}
              >
                {props.user.email}
              </LabelledContainer>
              <LabelledContainer
                fontWeight={600}
                text={t('newCompanyAdmin.newUser.form.phone', { ns: 'components' })}
              >
                {props.user.phoneNumber}
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'Skapad'}>
                {formattedDateTime(props.user.createdAt)}
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'Senast ändrad'}>
                {formattedDateTime(props.user.updatedAt)}
              </LabelledContainer>
              <LabelledContainer fontWeight={600} text={'Är organisationsadmin'}>
                {props.user.isOrganisationAdmin ? 'Ja' : 'Nej'}
              </LabelledContainer>
              {errorList}
            </Stack>
          </OverlayLoader>
        ),
        footer: {
          right: (
            <>
              <Button
                color={'primary'}
                loading={isPending}
                variant="contained"
                onClick={async () => {
                  if (props.user) {
                    const result = await updateUser({
                      email: props.user.email,
                      firstName: firstName,
                      lastName: lastName,
                      enabled: enabled,
                    });
                    props.onUserUpdated(result.data);
                    props.dialog.hide();
                  }
                }}
              >
                {'Uppdatera'}
              </Button>
              <Button loading={isPending} variant="text" onClick={onClose}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
